import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Navigation, MapPin, CheckCircle2, ChevronRight, Route } from "lucide-react";
import { CITY_CONTENT } from "../config/content";
import { BUSINESS, mapsDirUrl, mapsEmbedUrl } from "../config/business";
import SEO from "../components/SEO";
import { FadeUp, MaskedLine } from "../components/anim";

export default function LocationPage() {
  const { city } = useParams();
  const data = CITY_CONTENT[city];
  if (!data) return <Navigate to="/locations/modesto" replace />;

  const schema = [{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `https://${BUSINESS.domain}/` },
      { "@type": "ListItem", position: 2, name: `Smog Check ${data.name}`, item: `https://${BUSINESS.domain}/locations/${city}` },
    ],
  }];

  const others = BUSINESS.cities.filter((c) => c.slug !== city);

  return (
    <div data-testid="location-page">
      <SEO
        title={`Smog Check ${data.name}, CA | STAR Station | ${BUSINESS.name}`}
        description={`Fast STAR smog check for ${data.name}, CA drivers at ${BUSINESS.name}. ${data.drive} Walk-ins welcome — call ${BUSINESS.phoneDisplay}.`}
        keywords={`smog check ${data.name}, STAR smog ${data.name}, smog station ${data.name} CA`}
        path={`/locations/${city}`}
        schema={schema}
      />
      <div className="section pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-ink/50 font-body">
          <Link to="/" className="hover:text-volt">Home</Link><ChevronRight className="h-3 w-3" />
          <span className="text-ink">Smog Check {data.name}</span>
        </nav>
      </div>

      <section className="section py-8 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
        <div>
          <span className="chip">{data.primary ? "Primary Location" : `Serving ${data.name}, CA`}</span>
          <h1 className="mt-4 font-head font-extrabold text-4xl sm:text-5xl text-ink">
            <MaskedLine lines={[{ text: `Smog Check in`, className: "text-ink" }, { text: `${data.name}, CA`, className: "text-volt" }]} />
          </h1>
          <p className="mt-5 text-lg text-ink/70 font-body">{data.intro}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {data.roads.map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5 rounded-full bg-softblue px-3 py-1.5 text-xs font-semibold text-ink"><Route className="h-3.5 w-3.5 text-volt" />{r}</span>
            ))}
          </div>

          <FadeUp className="mt-8">
            <div className="rounded-2xl overflow-hidden border border-line shadow-sm">
              <iframe title={`Map ${data.name}`} src={mapsEmbedUrl()} className="w-full h-[320px]" loading="lazy" />
            </div>
          </FadeUp>

          <div className="mt-8 card-soft p-6">
            <h2 className="font-head font-bold text-xl text-ink">Why {data.name} drivers choose us</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {["STAR certified — DMV directed OK", "Most tests done in 15–20 min", "90-day free retest", "Registration & tags on the spot", "Diesel smog available", "Honest, upfront pricing"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-ink/80 font-body"><CheckCircle2 className="h-5 w-5 text-openg shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 space-y-4">
          <div className="card-soft p-6">
            <h3 className="font-head font-bold text-ink">Visit Our Station</h3>
            <p className="mt-2 text-sm text-ink/70 font-body inline-flex items-start gap-2"><MapPin className="h-5 w-5 text-volt shrink-0" />{BUSINESS.address.full}</p>
            <p className="mt-1 text-sm text-ink/60 font-body">{data.drive}</p>
            <div className="mt-4 flex flex-col gap-2">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt w-full"><Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}</a>
              <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="btn-cta w-full"><Navigation className="h-4 w-4" /> Get Directions</a>
            </div>
          </div>
          <div className="card-soft p-6">
            <h3 className="font-head font-bold text-ink">Other Areas We Serve</h3>
            <ul className="mt-3 space-y-2">
              {others.map((c) => (
                <li key={c.slug}><Link to={`/locations/${c.slug}`} className="text-sm font-body text-ink/80 hover:text-volt">Smog Check {c.name}</Link></li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
